import React, { CSSProperties, ReactElement, useEffect, useRef, useState } from 'react';
import { BorderedInput } from '@navi/web-ui/lib/primitives';
import InfiniteScroll from '../InfiniteScroll/InfiniteScroll';
import SearchIcon from '@icons/SearchIcon';
import CloseIcon from '../../assets/icons/CloseIcon';
import { Link } from 'react-router-dom';
import { getLink, isMac } from '../../utils/commonUtils';
import styles from './AutoComplete.module.scss';
import { FETCH_ABORT_REASON, fetchSearchedCases } from 'src/pages/Cases/CasesActions';
import { debounce } from '@navi/web-ui/lib/utils';
import cx from 'classnames';
import { AllCasesSummary } from 'src/pages/Cases/constants/CasesInterfaces';
import LoadingIcon from '@navi/web-ui/lib/icons/LoadingIcon/LoadingIcon';
import { logError } from 'src/utils/ApiHelper';
import { GenericObject } from '../../types/CommonConstans';
import toProperCase from '../../utils/toProperCase';
import { Toast } from '@navi/web-ui/lib/primitives';
import { addClickstreamEvent } from '../../service/clickStreamEventService';
import { CLICKSTREAM_EVENT_NAMES } from '../../service/clickStream.constant';
import {
  Directions,
  Tooltip as TooltipV2,
  TooltipContent,
  TooltipTrigger
} from '../TooltipV2/TooltipV2';
import { KEYBOARD_KEYS } from '@cp/src/constants/Common.constants';

const MAX_CASES = 7;
const delay = 500;
const MINIMUM_CHARACTERS_FOR_SEARCHING = 3;
const searchingKeys = ['customerName', 'accountNumber', 'phoneNumber'];
const spanOpeningTag = '<span class="bg_highlighter">';
const spanClosingTag = '</span>';
const BORDER_INPUT_COLLAPSED_WIDTH = '75%';
const BORDER_INPUT_EXPANDED_WIDTH = '90%';
const HEIGHT_OF_ONE_GLOBAL_SEARCH_CONTENT = 70;
const FORM_CONTROL_ID = 'global-search-input-id';
const PREFIX_GLOBAL_SEARCH = 'global-search-content-';
const MinimumNumberMessageComponent = (
  <label htmlFor={FORM_CONTROL_ID} className={styles.endMessage}>
    {' '}
    Please enter at least {MINIMUM_CHARACTERS_FOR_SEARCHING} characters
  </label>
);
const enum GLOBAL_SEARCH_ELEMENT_OBSERVABILITY_STATE {
  VISIBLE = 'VISIBLE',
  NOT_VISIBLE = 'NOT_VISIBLE'
}

const InputStyles = {
  textOverflow: 'unset',
  fontSize: '14px',
  marginLeft: '8px',
  marginRight: '0px',
  cursor: 'text'
} as CSSProperties;

const getHighlightedTagForFetchedData = (
  data: string,
  key: string,
  highlightObject: GenericObject,
  extraTag?: ReactElement
) => {
  if (highlightObject?.[key]) {
    const highlightStart = highlightObject?.[key]?.[0];
    const highlightEnd = highlightObject?.[key]?.[1];
    return `${data.substring(0, highlightStart)}${spanOpeningTag}${data?.substring(
      highlightStart,
      highlightEnd + 1
    )}${spanClosingTag}${data?.substring(highlightEnd + 1, data?.length)}${
      extraTag ? extraTag : ''
    }`;
  }

  return data;
};

const AutoComplete: React.FC = () => {
  const [userInput, setUserInput] = useState('');
  const [filteredSuggestions, setFilteredSuggestions] = useState<AllCasesSummary[]>([]);

  const [IsFocused, setIsFocused] = useState<boolean>(false);
  const [selectedItemIndex, setSelectedItemIndex] = useState<number>(-1);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState<string | null>(null);
  const nextPage = useRef<string | null>(null);
  const blurTimout = useRef<NodeJS.Timeout>();
  const InfiniteScrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isKeyPressed, setIsKeyPressed] = useState(false);
  const enum Visibility {
    ABOVE = 'ABOVE',
    BELOW = 'BELOW',
    BETWEEN = 'BETWEEN'
  }
  const infiniteScrollContainerObserver = useRef<IntersectionObserver | null>(null);
  const [selectedElementObservable, setSelectedElementObservable] =
    useState<GLOBAL_SEARCH_ELEMENT_OBSERVABILITY_STATE | null>(null);

  let fetchRequestController: AbortController | null = null;
  let fetchRequestControllerAbortReason: FETCH_ABORT_REASON | null = null;
  const fetchCases = async (page: string | null, search: string) => {
    try {
      setIsLoading(true);
      if (search.length < MINIMUM_CHARACTERS_FOR_SEARCHING) {
        if (fetchRequestController) {
          fetchRequestController.abort();
          fetchRequestControllerAbortReason = FETCH_ABORT_REASON.MINIMUM_CHARACTERS_REQUIRED;
        }

        return;
      }

      if (fetchRequestController) {
        fetchRequestController.abort();
        fetchRequestControllerAbortReason = FETCH_ABORT_REASON.MULTIPLE_REQUEST;
      }

      fetchRequestController = new AbortController();
      const { data } = await fetchSearchedCases(page, search, fetchRequestController);
      fetchRequestController = null;
      setIsLoading(false);

      nextPage.current = data?.pages?.nextPageToken;

      if (data?.pages?.nextPageToken === null || data?.data?.length <= MAX_CASES) {
        setHasMore(false);
      }

      addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_CaseList_SearchSuccess, {
        query: search,
        source: 'GLOBAL_SEARCH'
      });

      setFilteredSuggestions(previouseFilteredSuggestions => [
        ...previouseFilteredSuggestions,
        ...(data?.data || {})
      ]);
    } catch (err) {
      if (
        fetchRequestControllerAbortReason !== FETCH_ABORT_REASON.MULTIPLE_REQUEST &&
        fetchRequestControllerAbortReason !== FETCH_ABORT_REASON.MINIMUM_CHARACTERS_REQUIRED
      ) {
        setIsLoading(false);
        setHasMore(false);
        Toast.toast('Failed to fetch from server', { type: 'error' });
        logError(err, 'Getting error in globalSearching API call');
      }

      fetchRequestControllerAbortReason = null;
    }
  };

  const debouncedFetchCases = useRef(debounce(fetchCases, delay));

  const debounceChangeHandler = (page: string | null, search: string) => {
    debouncedFetchCases.current(page, search);
  };

  const handleFocus = (focus: boolean) => {
    setIsFocused(focus);
  };

  const handleBlur = () => {
    blurTimout.current = setTimeout(() => {
      handleFocus(false);
    }, 300);
  };

  const handleClearInput = () => {
    if (userInput.length && IsFocused) {
      setUserInput('');
      setPage(null);
      nextPage.current = null;
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value.length >= MINIMUM_CHARACTERS_FOR_SEARCHING) {
      setHasMore(true);
    }

    setUserInput(event.target.value.trim());
  };

  const focusOnSearch = () => {
    clearTimeout(blurTimout.current);
    handleFocus(true);
  };

  const fetchMoreData = function () {
    setIsLoading(true);
    setPage(nextPage.current);
  };

  const handleScrollBy = (top: number) => {
    document?.getElementsByClassName('infinite-scroll-component')[0]?.scrollBy({ top: top });
  };

  const getElementVisibility = (element: HTMLElement, referenceElement: HTMLElement) => {
    const elementTop = element?.getBoundingClientRect()?.top;
    const elementBottom = element?.getBoundingClientRect()?.bottom;
    const referenceElementTop = referenceElement?.getBoundingClientRect()?.top;
    const referenceElementBottom = referenceElement?.getBoundingClientRect()?.bottom;

    if (elementTop && elementBottom && referenceElementTop && referenceElementBottom) {
      if (elementTop < referenceElementTop) {
        return Visibility.ABOVE;
      } else if (elementBottom > referenceElementBottom) {
        return Visibility.BELOW;
      } else {
        return Visibility.BETWEEN;
      }
    }
  };

  const handleScrollForSelectedElement = (elementId: number, smooth = false) => {
    const element = document.getElementById(PREFIX_GLOBAL_SEARCH + elementId);
    if (element && InfiniteScrollRef?.current) {
      const visibility: Visibility | undefined = getElementVisibility(
        element,
        InfiniteScrollRef?.current
      );

      if (visibility === Visibility.ABOVE) {
        element.scrollIntoView(
          smooth ? { block: 'start', behavior: 'smooth' } : { block: 'start' }
        );
      } else if (visibility === Visibility.BELOW) {
        element.scrollIntoView(smooth ? { block: 'end', behavior: 'smooth' } : { block: 'end' });
      }
    }
  };

  const clickAndBlurSelectedElement = (element: HTMLElement) => {
    element.click();
    inputRef?.current?.blur();
  };

  const handleKeydown = (event: KeyboardEvent) => {
    if ((event.ctrlKey || event.metaKey) && event.key === 'g') {
      event.preventDefault();
      focusOnSearch();
      inputRef?.current?.focus();
      setIsKeyPressed(true);
    } else if (event.key === KEYBOARD_KEYS.ARROW_DOWN) {
      event.preventDefault();
      if (selectedItemIndex >= filteredSuggestions?.length - 1) {
        handleScrollBy(HEIGHT_OF_ONE_GLOBAL_SEARCH_CONTENT);
        return;
      }
      const lastKeyDownIndex = selectedItemIndex + 1;
      setSelectedItemIndex(selectedItemIndex => selectedItemIndex + 1);
      setIsKeyPressed(true);
      handleScrollForSelectedElement(lastKeyDownIndex);
    } else if (event.key === KEYBOARD_KEYS.ARROW_UP) {
      event.preventDefault();
      if (selectedItemIndex <= 0) return;
      const lastKeyDownIndex = selectedItemIndex - 1;
      setSelectedItemIndex(selectedItemIndex => selectedItemIndex - 1);
      setIsKeyPressed(true);
      handleScrollForSelectedElement(lastKeyDownIndex);
    } else if (event.key === KEYBOARD_KEYS.ENTER) {
      if (selectedItemIndex === -1 || inputRef?.current !== document.activeElement) return;
      const element = document.getElementById(PREFIX_GLOBAL_SEARCH + selectedItemIndex);
      if (element && InfiniteScrollRef?.current) {
        if (getElementVisibility(element, InfiniteScrollRef?.current) !== Visibility.BETWEEN) {
          setSelectedElementObservable(GLOBAL_SEARCH_ELEMENT_OBSERVABILITY_STATE.NOT_VISIBLE);
          handleScrollForSelectedElement(selectedItemIndex, true);
        } else {
          clickAndBlurSelectedElement(element);
        }
      }
    }
  };

  const handleMouseMove = () => setIsKeyPressed(false);

  useEffect(() => {
    if (!isKeyPressed) () => document?.removeEventListener('mousemove', handleMouseMove);

    document?.addEventListener('mousemove', handleMouseMove);
    return () => document?.removeEventListener('mousemove', handleMouseMove);
  }, [isKeyPressed]);

  useEffect(() => {
    document?.removeEventListener('keydown', handleKeydown);
    document?.addEventListener('keydown', handleKeydown);
    return () => document?.removeEventListener('keydown', handleKeydown);
  }, [InfiniteScrollRef, selectedItemIndex, filteredSuggestions]);

  useEffect(() => {
    setSelectedItemIndex(-1);
    const search = userInput?.trim();
    if (search !== '') {
      nextPage.current = null;
      setPage(null);
      setFilteredSuggestions([]);
      debounceChangeHandler(page, search);
    }
  }, [userInput]);

  useEffect(() => {
    const search = userInput?.trim();
    if (search !== '' && page != null) {
      fetchCases(page, search);
    }
  }, [page]);

  useEffect(() => {
    if (!InfiniteScrollRef?.current) return;

    infiniteScrollContainerObserver.current = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const element = entry.target as HTMLElement;
            const visibility: Visibility | undefined = getElementVisibility(
              element,
              InfiniteScrollRef?.current as HTMLElement
            );

            if (visibility === Visibility.BETWEEN) {
              setSelectedElementObservable(GLOBAL_SEARCH_ELEMENT_OBSERVABILITY_STATE.VISIBLE);
            }
          }
        });
      },
      {
        root: InfiniteScrollRef.current,
        rootMargin: '0px',
        threshold: 1.0
      }
    );
  }, [InfiniteScrollRef?.current]);

  useEffect(() => {
    if (!InfiniteScrollRef?.current) return;
    const elementToObserve = document.getElementById(
      PREFIX_GLOBAL_SEARCH + selectedItemIndex
    ) as HTMLElement;
    if (!elementToObserve) return;

    if (selectedElementObservable === GLOBAL_SEARCH_ELEMENT_OBSERVABILITY_STATE.VISIBLE) {
      infiniteScrollContainerObserver.current?.unobserve(elementToObserve);

      InfiniteScrollRef.current.onwheel = () => {
        return true;
      };

      clickAndBlurSelectedElement(elementToObserve);
      setSelectedElementObservable(null);
    } else if (
      selectedElementObservable === GLOBAL_SEARCH_ELEMENT_OBSERVABILITY_STATE.NOT_VISIBLE
    ) {
      infiniteScrollContainerObserver.current?.observe(elementToObserve);

      InfiniteScrollRef.current.onwheel = () => {
        return false;
      };
    }
  }, [InfiniteScrollRef?.current, selectedElementObservable, selectedItemIndex]);

  useEffect(() => {
    if (InfiniteScrollRef.current == null || selectedItemIndex === -1) return;
    const elementToObserve = document.getElementById(
      PREFIX_GLOBAL_SEARCH + selectedItemIndex
    ) as HTMLElement;
    if (!elementToObserve) return;
    const visibility: Visibility | undefined = getElementVisibility(
      elementToObserve,
      InfiniteScrollRef?.current as HTMLElement
    );

    if (
      !IsFocused &&
      visibility !== Visibility.BETWEEN &&
      selectedElementObservable === GLOBAL_SEARCH_ELEMENT_OBSERVABILITY_STATE.NOT_VISIBLE
    ) {
      infiniteScrollContainerObserver.current?.unobserve(elementToObserve);
      elementToObserve.click();
      setSelectedElementObservable(null);
      InfiniteScrollRef.current.onwheel = () => {
        return true;
      };
    }
  }, [IsFocused, selectedItemIndex]);

  const handleSearchItemClick = (elementIndex: number) => {
    setSelectedItemIndex(prevSelectedItemIndex => {
      const prevSelectedItem = document.getElementById(
        PREFIX_GLOBAL_SEARCH + prevSelectedItemIndex
      );
      prevSelectedItem && infiniteScrollContainerObserver.current?.unobserve(prevSelectedItem);

      if (InfiniteScrollRef?.current) {
        InfiniteScrollRef.current.onwheel = () => {
          return true;
        };
      }

      return elementIndex;
    });

    handleBlur();
  };

  const getContent = (suggestions: AllCasesSummary[], isLocalData?: boolean) => {
    const startIndex = 0;
    return suggestions?.map((details, index) => (
      <Link
        key={details.accountNumber}
        className={styles.searchedCustomerCase}
        to={getLink({
          accountNumber: details?.accountNumber,
          customerReferenceId: details?.customerId,
          caseReferenceId: details?.caseReferenceId || '',
          caseBusinessVertical: details?.businessVertical || ''
        })}
        tabIndex={-1}
        onContextMenu={() => {
          clearTimeout(blurTimout.current);
        }}
      >
        <div
          id={PREFIX_GLOBAL_SEARCH + (index + startIndex)}
          className={cx(styles.customerDetailsContainer, {
            [styles.selected]: index + startIndex === selectedItemIndex,
            [styles.notSelected]: !isKeyPressed && index + startIndex !== selectedItemIndex
          })}
          onClick={() => {
            handleSearchItemClick(index + startIndex);
          }}
        >
          {details?.name?.length && details?.name?.length >= 19 ? (
            <TooltipV2 placement={Directions.TOP}>
              <TooltipTrigger tooltipTriggerClassName="tooltipTriggerWrapper">
                <div
                  className={cx(styles.customerName)}
                  dangerouslySetInnerHTML={{
                    __html: getHighlightedTagForFetchedData(
                      details?.name || '',
                      'name',
                      details?.queryResultHighlighter
                    )
                  }}
                ></div>
              </TooltipTrigger>
              {!isKeyPressed ? (
                <TooltipContent className={cx('tooltipWrapper', styles.autoCompleteTooltipWrapper)}>
                  {details?.name}
                </TooltipContent>
              ) : null}
            </TooltipV2>
          ) : (
            <div
              className={styles.customerName}
              dangerouslySetInnerHTML={{
                __html: getHighlightedTagForFetchedData(
                  details?.name || '',
                  'name',
                  details?.queryResultHighlighter
                )
              }}
            ></div>
          )}

          <div className={styles.customerDetails}>
            <div className={styles.customerLanNumber}>
              <span className={styles.text}>LAN</span>
              <span
                className={styles.value}
                dangerouslySetInnerHTML={{
                  __html: getHighlightedTagForFetchedData(
                    details?.accountNumber,
                    'accountNumber',
                    details?.queryResultHighlighter
                  )
                }}
              ></span>
            </div>
            <div className={cx(styles.customerTelephoneNumber, 'rr-block')}>
              <span
                className={styles.value}
                dangerouslySetInnerHTML={{
                  __html: getHighlightedTagForFetchedData(
                    details?.telephoneNumber || '',
                    'telephoneNumber',
                    details?.queryResultHighlighter
                  )
                }}
              ></span>
              <span className={styles.source}>
                ({toProperCase(details?.telephoneSource || '')})
              </span>
            </div>
          </div>
        </div>
      </Link>
    ));
  };

  const getEndMessage = () => {
    if (userInput.length < MINIMUM_CHARACTERS_FOR_SEARCHING) {
      return MinimumNumberMessageComponent;
    }
    if (isLoading) {
      return (
        <label htmlFor={FORM_CONTROL_ID} className={cx(styles.endMessage, styles.loading)}>
          <LoadingIcon />
        </label>
      );
    }
    if (filteredSuggestions?.length === 0 && userInput.length && !isLoading) {
      return (
        <label htmlFor={FORM_CONTROL_ID} className={styles.endMessage}>
          {' '}
          No results found
        </label>
      );
    }

    if (filteredSuggestions?.length < MAX_CASES && !isLoading) {
      return null;
    }

    if (filteredSuggestions?.length == MAX_CASES && !isLoading) {
      return (
        <label htmlFor={FORM_CONTROL_ID} className={styles.endMessage}>
          No more results!{' '}
        </label>
      );
    }

    if (nextPage.current === null) {
      return filteredSuggestions?.length >= MAX_CASES ? (
        <label htmlFor={FORM_CONTROL_ID} className={styles.endMessage}>
          No more results!{' '}
        </label>
      ) : null;
    }

    return (
      <label htmlFor={FORM_CONTROL_ID} className={cx(styles.endMessage, styles.loading)}>
        <LoadingIcon />
      </label>
    );
  };

  const showSearchedCustomers = IsFocused && !!userInput?.length;

  return (
    <div className={styles.globalSearchContainer}>
      <label htmlFor={FORM_CONTROL_ID} style={{ display: 'flex' }}>
        <div onFocus={focusOnSearch} onBlur={handleBlur}>
          <div className={styles.searchInputContainer}>
            <BorderedInput
              ref={inputRef}
              type="text"
              id={FORM_CONTROL_ID}
              value={userInput}
              placeholder={IsFocused ? 'Search here...' : isMac ? 'Cmd + G' : 'Ctrl + G' + ''}
              LeftInputAdornment={
                <label htmlFor={FORM_CONTROL_ID}>
                  {<SearchIcon height={20} width={20} style={{ cursor: 'text' }} />}
                </label>
              }
              RightInputAdornment={
                IsFocused && (
                  <label
                    htmlFor={FORM_CONTROL_ID}
                    onClick={handleClearInput}
                    className={cx({
                      [styles.crossIconContainer]: true,
                      [styles.isBlank]: !userInput?.length
                    })}
                  >
                    {userInput?.length && IsFocused ? <CloseIcon height={20} width={20} /> : null}
                  </label>
                )
              }
              onChange={handleInputChange}
              inputSize="full-width"
              containerClassName={cx(
                styles.borderInputContainer,
                'transition-[width]',
                'duration-300',
                {
                  [styles.borderInputContainerFocused]: IsFocused,
                  [styles.borderInputContainerNotFocused]: !IsFocused
                }
              )}
              style={{
                width: IsFocused ? BORDER_INPUT_EXPANDED_WIDTH : BORDER_INPUT_COLLAPSED_WIDTH,
                ...InputStyles
              }}
              autoFocus={IsFocused}
              autoComplete="off"
            />
          </div>
        </div>
      </label>

      <div
        className={cx(styles.infiniteScrollContainer, {
          [styles.showInfinteScroll]: showSearchedCustomers
        })}
        ref={InfiniteScrollRef}
      >
        <InfiniteScroll
          dataLength={filteredSuggestions?.length}
          next={() => {
            fetchMoreData();
          }}
          scrollThreshold={0.6}
          hasMore={hasMore}
          loader={
            userInput.length < MINIMUM_CHARACTERS_FOR_SEARCHING ? (
              MinimumNumberMessageComponent
            ) : (
              <label htmlFor={FORM_CONTROL_ID} className={styles.loading}>
                <LoadingIcon />
              </label>
            )
          }
          maxHeight={HEIGHT_OF_ONE_GLOBAL_SEARCH_CONTENT * MAX_CASES}
          endMessage={getEndMessage()}
          outerContainerclassName={styles.infiniteScrollOuterDivContainer}
        >
          {getContent(filteredSuggestions)}
        </InfiniteScroll>
      </div>
    </div>
  );
};

export default AutoComplete;
